import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import PageHeader from '../components/PageHeader';

// Import the header image
import headerImage from '../assets/images/1.jpg';

// Import Wedding Services images
import wedding1 from '../assets/images/Wedding Services/1.jpg';
import wedding2 from '../assets/images/Wedding Services/2.jpg';
import wedding3 from '../assets/images/Wedding Services/3.jpg';
import wedding4 from '../assets/images/Wedding Services/4.jpg';
import wedding5 from '../assets/images/Wedding Services/5.jpg';
import wedding6 from '../assets/images/Wedding Services/6.jpg';

// Import Drum Institute images
import institute1 from '../assets/images/Drum Institute/1.jpg';
import institute2 from '../assets/images/Drum Institute/2.jpg';
import institute3 from '../assets/images/Drum Institute/3.jpg';
import institute4 from '../assets/images/Drum Institute/4.jpg';
import institute5 from '../assets/images/Drum Institute/5.jpg';
import institute6 from '../assets/images/Drum Institute/6.jpg';
import institute7 from '../assets/images/Drum Institute/7.jpg';
import institute8 from '../assets/images/Drum Institute/8.jpg';
import institute9 from '../assets/images/Drum Institute/9.jpg';
import institute10 from '../assets/images/Drum Institute/10.jpg';
import institute11 from '../assets/images/Drum Institute/11.jpg';

// Import Corporate Events images
import corporate1 from '../assets/images/Coporate Events/1.png';
import corporate2 from '../assets/images/Coporate Events/2.jpg';
import corporate3 from '../assets/images/Coporate Events/3.jpg';
import corporate4 from '../assets/images/Coporate Events/4.jpg';
import corporate5 from '../assets/images/Coporate Events/5.jpg';
import corporate6 from '../assets/images/Coporate Events/6.jpg';
import corporate7 from '../assets/images/Coporate Events/7.jpg';

const GalleryPage: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>("All");
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  // Gallery categories
  const categories = [
    "All",
    "Institute & Training",
    "Corporate Events",
    "Wedding Ceremonies",
    "Student Achievements",
    "International Performances"
  ];

  // Gallery data structure with actual images from assets
  const galleryImages = [
    // Institute & Training
    {
      id: 1,
      src: institute1,
      alt: "Drum training session",
      category: "Institute & Training",
      featured: true
    },
    {
      id: 2,
      src: institute2,
      alt: "Students learning traditional drums",
      category: "Institute & Training",
      featured: false
    },
    {
      id: 3,
      src: institute3,
      alt: "Group drum training",
      category: "Institute & Training",
      featured: false
    },
    {
      id: 4,
      src: institute4,
      alt: "Hands-on drum instruction",
      category: "Institute & Training",
      featured: false
    },
    {
      id: 5,
      src: institute5,
      alt: "Dance training session",
      category: "Institute & Training",
      featured: false
    },
    
    // Student Achievements
    {
      id: 6,
      src: institute6,
      alt: "Student performance showcase",
      category: "Student Achievements",
      featured: true
    },
    {
      id: 7,
      src: institute7,
      alt: "Student awards ceremony",
      category: "Student Achievements",
      featured: false
    },
    {
      id: 8,
      src: institute8,
      alt: "Graduating student performers",
      category: "Student Achievements",
      featured: false
    },
    {
      id: 9,
      src: institute9,
      alt: "Student showcase event",
      category: "Student Achievements",
      featured: false
    },
    {
      id: 10,
      src: institute10,
      alt: "Student competition winners",
      category: "Student Achievements",
      featured: false
    },
    {
      id: 11,
      src: institute11,
      alt: "Advanced student demonstration",
      category: "Student Achievements",
      featured: false
    },
    
    // Corporate Events
    {
      id: 12,
      src: corporate1,
      alt: "Corporate event performance",
      category: "Corporate Events",
      featured: true
    },
    {
      id: 13,
      src: corporate2,
      alt: "Corporate celebration performance",
      category: "Corporate Events",
      featured: false
    },
    {
      id: 14,
      src: corporate3,
      alt: "Company annual event performance",
      category: "Corporate Events",
      featured: false
    },
    {
      id: 15,
      src: corporate4,
      alt: "Stage performance for corporate event",
      category: "Corporate Events",
      featured: false
    },
    
    // International Performances
    {
      id: 16,
      src: corporate5,
      alt: "International cultural exchange",
      category: "International Performances",
      featured: true
    },
    {
      id: 17,
      src: corporate6,
      alt: "Overseas performance showcase",
      category: "International Performances",
      featured: false
    },
    {
      id: 18,
      src: corporate7,
      alt: "International festival performance",
      category: "International Performances",
      featured: false
    },
    
    // Wedding Ceremonies
    {
      id: 19,
      src: wedding1,
      alt: "Wedding ceremony performance",
      category: "Wedding Ceremonies",
      featured: true
    },
    {
      id: 20,
      src: wedding2,
      alt: "Traditional wedding dancers",
      category: "Wedding Ceremonies",
      featured: true
    },
    {
      id: 21,
      src: wedding3,
      alt: "Poruwa ceremony",
      category: "Wedding Ceremonies",
      featured: false
    },
    {
      id: 22,
      src: wedding4,
      alt: "Wedding drumming performance",
      category: "Wedding Ceremonies",
      featured: false
    },
    {
      id: 23,
      src: wedding5,
      alt: "Ceremonial dancers",
      category: "Wedding Ceremonies",
      featured: false
    },
    {
      id: 24,
      src: wedding6,
      alt: "Traditional wedding setup",
      category: "Wedding Ceremonies",
      featured: false
    }
  ];

  const filteredImages = activeCategory === "All" 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  const featuredImages = galleryImages.filter(img => img.featured);

  const openModal = (imageSrc: any) => {
    setSelectedImage(imageSrc);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <>
      <PageHeader
        title="Gallery"
        subtitle="Explore our performances, training sessions, and cultural events"
        backgroundImage={headerImage}
      />

      {/* Featured Gallery Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900">
              Featured <span className="text-orange-600">Highlights</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              A selection of our most iconic performances and events
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {featuredImages.map((image, index) => (
              <motion.div 
                key={image.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative rounded-lg overflow-hidden shadow-lg h-80 group cursor-pointer"
                onClick={() => openModal(image.src)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent opacity-70"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="bg-orange-600 text-white text-xs font-bold px-2 py-1 rounded-full mb-2 inline-block">
                    {image.category}
                  </div>
                  <h3 className="text-lg font-bold drop-shadow-md">{image.alt}</h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Full Gallery Section */}
      <section id="gallery" className="py-16 bg-orange-50" ref={ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900">
              Complete <span className="text-orange-600">Gallery</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              Browse through our entire collection of performances and events
            </p>
          </motion.div>

          {/* Category filter buttons */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category, index) => (
              <motion.button
                key={category}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === category
                    ? 'bg-orange-600 text-white shadow-md'
                    : 'bg-white text-gray-800 hover:bg-gray-100'
                }`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </motion.button>
            ))}
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filteredImages.map((image) => (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                layout
                className="relative overflow-hidden rounded-lg group cursor-pointer h-64 shadow-md hover:shadow-xl transition-shadow duration-300"
                onClick={() => openModal(image.src)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent opacity-0 group-hover:opacity-70 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <div className="text-xs font-semibold bg-orange-600 inline-block px-2 py-1 rounded-full mb-1">
                    {image.category}
                  </div>
                  <h3 className="text-sm font-medium">{image.alt}</h3>
                </div>
              </motion.div>
            ))}
          </div>
          
          {filteredImages.length === 0 && (
            <div className="bg-white rounded-lg p-12 text-center">
              <svg className="h-16 w-16 text-gray-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No images found</h3>
              <p className="mt-1 text-gray-500">No images available in this category.</p>
            </div>
          )}
        </div>
      </section>
      
      {/* Modal for full-size image */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
            onClick={closeModal}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-5xl max-h-[80vh] overflow-hidden rounded-lg"
            >
              <button 
                className="absolute top-2 right-2 z-10 text-white bg-red-600 rounded-full p-2 shadow-lg"
                onClick={(e) => { e.stopPropagation(); closeModal(); }}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <img 
                src={selectedImage} 
                alt="Gallery full view" 
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default GalleryPage;
